import { useState, useMemo, useEffect, useRef } from 'react';
import { DataTable, DataTableFilterMeta, DataTableFilterEvent, DataTableSortEvent } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Message } from 'primereact/message';
import { FilterMatchMode } from 'primereact/api';
import { Check } from '../../types';
import { usePersistentState } from '../../hooks';
import { formatCurrency, formatDate, amountFilterFunction } from '../../utils';

interface UnallocatedChecksPanelProps {
  clientId: string;
  checks: Check[];
  loading: boolean;
  selectionMode?: 'multiple' | null;
  selectedCheckIds?: Set<string>;
  onSelectionChange?: (checkIds: Set<string>) => void;
  selectedLedgerEntryDate?: string | null;
  externalAmountFilter?: string | null;
}

export default function UnallocatedChecksPanel({
  checks,
  loading,
  selectionMode,
  selectedCheckIds,
  onSelectionChange,
  selectedLedgerEntryDate,
  externalAmountFilter,
}: UnallocatedChecksPanelProps) {
  const [error] = useState<string | null>(null);
  const tableRef = useRef<any>(null);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(25);
  const defaultFilters: DataTableFilterMeta = {
    check_date: { value: null, matchMode: FilterMatchMode.CONTAINS },
    amount: { value: null, matchMode: 'custom_amount' as any },
    check_number: { value: null, matchMode: FilterMatchMode.CONTAINS },
    payee_name: { value: null, matchMode: FilterMatchMode.CONTAINS },
    memo: { value: null, matchMode: FilterMatchMode.CONTAINS },
  };
  const [storedFilters, setFilters] = usePersistentState<DataTableFilterMeta>(
    'reconciliation:unallocatedChecks:filters',
    defaultFilters
  );
  // Ensure matchMode from defaults is always used (handles localStorage with old matchMode)
  const filters = useMemo(() => {
    return Object.keys(defaultFilters).reduce((acc, key) => {
      const defaultFilter = defaultFilters[key];
      const storedFilter = storedFilters[key];
      if (defaultFilter && typeof defaultFilter === 'object' && 'matchMode' in defaultFilter) {
        acc[key] = {
          ...defaultFilter,
          value: storedFilter && 'value' in storedFilter ? storedFilter.value : defaultFilter.value,
        };
      } else {
        acc[key] = storedFilter ?? defaultFilter;
      }
      return acc;
    }, {} as DataTableFilterMeta);
  }, [storedFilters]);
  const [sortState, setSortState] = usePersistentState<{
    sortField: string;
    sortOrder: 1 | 0 | -1 | null | undefined;
  }>('reconciliation:unallocatedChecks:sort', {
    sortField: 'check_date',
    sortOrder: 1,
  });

  // Apply external amount filter when it changes
  useEffect(() => {
    if (externalAmountFilter !== undefined) {
      setFilters((prev) => ({
        ...prev,
        amount: { value: externalAmountFilter, matchMode: 'custom_amount' as any },
      }));
      // Reset to first page when filter is applied
      if (externalAmountFilter) {
        setFirst(0);
      }
    }
  }, [externalAmountFilter]);

  const dateTemplate = (rowData: Check) => {
    return formatDate(rowData.check_date);
  };

  const amountTemplate = (rowData: Check) => {
    return formatCurrency(rowData.amount);
  };

  // Helper to check if any filters are active
  const hasActiveFilters = useMemo(() => {
    const result = Object.values(filters).some(filter => {
      if ('value' in filter) {
        return filter.value !== null && filter.value !== '';
      }
      return false;
    });
    return result;
  }, [filters]);

  const getSelectedChecks = () => {
    if (selectionMode === 'multiple' && selectedCheckIds) {
      return checks.filter((c) => c.id && selectedCheckIds.has(c.id));
    }
    return [];
  };

  const handleSelectionChange = (e: any) => {
    if (selectionMode === 'multiple' && onSelectionChange) {
      const selectedChecks = e.value as Check[];
      const newSet = new Set<string>();
      selectedChecks.forEach((c) => {
        if (c.id) newSet.add(c.id);
      });
      onSelectionChange(newSet);
    }
  };

  const dateRange = useMemo(() => {
    const validDates = checks
      .map((c) => c.check_date)
      .filter((date): date is string => date !== null && date !== undefined)
      .map((date) => new Date(date))
      .filter((date) => !isNaN(date.getTime()));

    if (validDates.length === 0) return null;

    const minDate = new Date(Math.min(...validDates.map((d) => d.getTime())));
    const maxDate = new Date(Math.max(...validDates.map((d) => d.getTime())));

    return {
      min: minDate.toLocaleDateString(),
      max: maxDate.toLocaleDateString(),
    };
  }, [checks]);

  // Normalize date to just the date string (YYYY-MM-DD) to avoid timezone issues
  const normalizeDateString = (dateStr: string): string => {
    return dateStr.split('T')[0]; // Get just the YYYY-MM-DD part
  };

  // Sort checks by date (same as table sorting)
  const sortedChecks = useMemo(() => {
    return [...checks].sort((a, b) => {
      if (!a.check_date) return 1;
      if (!b.check_date) return -1;
      return a.check_date.localeCompare(b.check_date);
    });
  }, [checks]);

  // Find checks matching or closest to the selected ledger entry date
  const { matchingCheckIndex, matchingCheckId, isExactMatch } = useMemo(() => {
    if (!selectedLedgerEntryDate || sortedChecks.length === 0) {
      return { matchingCheckIndex: -1, matchingCheckId: null, isExactMatch: false };
    }

    const normalizedEntryDate = normalizeDateString(selectedLedgerEntryDate);

    // First try to find exact match
    const exactMatchIndex = sortedChecks.findIndex((c) => {
      if (!c.check_date) return false;
      const normalizedCheckDate = normalizeDateString(c.check_date);
      return normalizedCheckDate === normalizedEntryDate;
    });

    if (exactMatchIndex >= 0) {
      return {
        matchingCheckIndex: exactMatchIndex,
        matchingCheckId: sortedChecks[exactMatchIndex].id,
        isExactMatch: true,
      };
    }

    // No exact match - find closest date
    let closestIndex = -1;
    let smallestDiff = Infinity;
    const entryTime = new Date(normalizedEntryDate).getTime();

    sortedChecks.forEach((c, idx) => {
      if (!c.check_date) return;
      const checkTime = new Date(normalizeDateString(c.check_date)).getTime();
      const diff = Math.abs(checkTime - entryTime);

      if (diff < smallestDiff) {
        smallestDiff = diff;
        closestIndex = idx;
      }
    });

    return {
      matchingCheckIndex: closestIndex,
      matchingCheckId: closestIndex >= 0 ? sortedChecks[closestIndex].id : null,
      isExactMatch: false,
    };
  }, [selectedLedgerEntryDate, sortedChecks]);

  // Scroll to matching check when ledger entry is selected
  useEffect(() => {
    // Don't change pages when filters are active - the matchingCheckIndex is based on
    // the unfiltered array, but DataTable is showing filtered results
    if (hasActiveFilters) {
      return;
    }

    if (matchingCheckIndex >= 0) {
      // Calculate which page the matching check is on
      const pageIndex = Math.floor(matchingCheckIndex / rows);
      const targetFirst = pageIndex * rows;

      if (targetFirst !== first) {
        setFirst(targetFirst);
      } else {
        // If we're already on the correct page, scroll immediately
        scrollToHighlightedRow();
      }
    }
  }, [matchingCheckIndex, rows, first, hasActiveFilters]);

  // Scroll to the highlighted row after page change or selection change
  useEffect(() => {
    // Don't scroll when filters are active
    if (hasActiveFilters) {
      return;
    }

    if (matchingCheckIndex >= 0 && first >= 0 && selectedLedgerEntryDate) {
      // Small delay to ensure DataTable has rendered the new page/selection
      const timer = setTimeout(() => {
        scrollToHighlightedRow();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [first, matchingCheckIndex, selectedLedgerEntryDate, hasActiveFilters]);

  const scrollToHighlightedRow = () => {
    // Don't scroll to highlighted row if filters are active
    if (hasActiveFilters) {
      return;
    }

    if (!tableRef.current) return;

    // Find the highlighted row element
    const tableElement = (tableRef.current as any).getElement();
    if (!tableElement) return;

    const highlightedRow = tableElement.querySelector('.highlight-row');
    if (!highlightedRow) return;

    // Find the scrollable body container (created by scrollable prop)
    const scrollBody = tableElement.querySelector('.p-datatable-scrollable-body');
    if (scrollBody) {
      // Calculate the position of the highlighted row relative to the scroll container
      const rowRect = highlightedRow.getBoundingClientRect();
      const scrollRect = scrollBody.getBoundingClientRect();
      const scrollTop = scrollBody.scrollTop;

      // Position the row 1/2 from the top of the visible area for better visibility
      const offset = scrollRect.height / 2;
      const targetScroll = scrollTop + (rowRect.top - scrollRect.top) - offset;

      scrollBody.scrollTo({ top: Math.max(0, targetScroll), behavior: 'smooth' });
    } else {
      // Fallback to regular scrollIntoView if not using scrollable mode
      highlightedRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const handleFilter = (event: DataTableFilterEvent) => {
    setFilters(event.filters);
  };

  const handleSort = (event: DataTableSortEvent) => {
    setSortState({
      sortField: event.sortField ?? sortState.sortField,
      sortOrder: event.sortOrder ?? sortState.sortOrder,
    });
  };

  return (
    <>
      {error && <Message severity="error" text={error} />}

      {checks.length === 0 && !loading ? (
        <Message
          severity="info"
          text="No unallocated checks found. All checks have been assigned to ledger entries."
        />
      ) : (
        <>
          <h3>
            Checks
            {dateRange && (
              <span className="date-range">
                ({dateRange.min} - {dateRange.max})
              </span>
            )}
          </h3>
          <DataTable
            ref={tableRef}
            value={checks}
            dataKey="id"
            className="highlight-table"
            size="small"
            scrollable
            scrollHeight="flex"
            paginator
            first={first}
            rows={rows}
            selectionMode="checkbox"
            onPage={(e) => {
              setFirst(e.first);
              setRows(e.rows);
            }}
            sortField={sortState.sortField}
            sortOrder={sortState.sortOrder ?? undefined}
            rowsPerPageOptions={[25, 50, 100]}
            paginatorTemplate={{
              layout:
                'RowsPerPageDropdown FirstPageLink PrevPageLink NextPageLink LastPageLink CurrentPageReport',
              CurrentPageReport: (options) => (
                <span className="paginator-current">
                  Showing {options.first}-{options.last} of <b>{options.totalRecords}</b> checks
                </span>
              ),
            }}
            loading={loading}
            filters={filters}
            onFilter={handleFilter}
            filterDisplay="menu"
            onSort={handleSort}
            stripedRows
            rowClassName={(data) => {
              // Check if this row is the matching check
              if (matchingCheckId && data.id === matchingCheckId) {
                // If it's an exact match, use primary highlight
                if (isExactMatch) {
                  return 'highlight-row';
                }
                // If it's the closest match (not exact), use orange highlight
                return 'highlight-row highlight-row-closest';
              }

              return '';
            }}
            selection={getSelectedChecks()}
            onSelectionChange={handleSelectionChange}
          >
            <Column selectionMode="multiple" style={{ width: '3%' }} />
            <Column
              field="check_number"
              header="#"
              style={{ width: '10%' }}
              sortable
              filter
              filterPlaceholder="Search by check #"
              showFilterMatchModes={false}
            />            
            <Column
              field="check_date"
              header="Date"
              style={{ width: '10%' }}
              sortable
              body={dateTemplate}
              filter
              filterPlaceholder="Search by date"
              showFilterMatchModes={false}
            />
            <Column
              field="amount"
              header="Amount"
              style={{ width: '15%' }}
              sortable
              body={amountTemplate}
              filter
              filterPlaceholder="Search by amount"
              showFilterMatchModes={false}
              filterFunction={amountFilterFunction}
            />
            <Column
              field="payee_name"
              header="Payee"
              sortable
              filter
              style={{ width: '20%' }}              
              filterPlaceholder="Search by payee"
              showFilterMatchModes={false}
            />
            <Column
              field="memo"
              header="Memo"
              sortable
              filter
              filterPlaceholder="Search by memo"
              showFilterMatchModes={false}
            />
          </DataTable>
        </>
      )}
    </>
  );
}
